<template>
  <div class="login">
    <LogoHeader :org="organizationDetails" />
    <div
      alt
      class="logo-wrapper-gc"
      :style="{ backgroundImage: `url(${require('@/assets/images/logo-color.jpg')}` }"
    ></div>
    <form @submit.prevent="handleSubmit" class="form">
      <md-field :class="{ 'md-invalid': this.errors }">
        <label v-translate>Personal email address</label>
        <md-input tabindex="1" required v-model="email" name="email"></md-input>
        <span :class="['md-error', 'ruby-font']">
          {{ errors && errors.email ? errors.email[0] : '' }}
        </span>
      </md-field>
      <md-field :class="{ 'md-invalid': this.errors }">
        <label v-translate>Password</label>
        <md-input tabindex="2" type="password" required v-model="password"></md-input>
        <span :class="['md-error', 'ruby-font']">
          {{ errors && errors.password ? errors.password[0] : '' }}
        </span>
        <span class="api-error" :class="['md-error', 'ruby-font']" v-if="Array.isArray(errors)">
          {{ errors[0] }}
        </span>
      </md-field>
      <div class="forgot-password">
        <router-link tabindex="4" :to="{ name: 'PasswordResetRequest' }" v-translate>
          forgot password?
        </router-link>
      </div>
      <LoadingSpinnerButton
        type="submit"
        tabindex="3"
        :loading="loading"
        :text="text"
        class="button"
      />
      <div class="create-account-cta">
        <span>
          <translate>New here?</translate>&nbsp;
          <router-link tabindex="5" :to="{ name: 'Verification' }" v-translate
            >Join Now</router-link
          >
        </span>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Auth from '@/services/auth'
import User from '@/services/users'
import Admin from '@/services/admins'
import Employee from '@/services/employees'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import LogoHeader from '@/components/LogoHeader'

export default {
  name: 'Login',
  components: {
    LoadingSpinnerButton,
    LogoHeader,
  },
  data() {
    return {
      email: '',
      password: '',
      errors: null,
      loading: false,
    }
  },
  methods: {
    handleSubmit() {
      this.loginUser(this.email, this.password)
    },
    async loginUser(email, password) {
      this.loading = true
      this.errors = null
      let res
      try {
        res = await Auth.login(email, password)
      } catch (err) {
        this.errors = err.response.data
        this.loading = false
        return
      }

      let user = User.fromAPI(res.data)

      // If the user is admin/staff, check if they need to be two-factor verified. Otherwise proceed as normal.
      if (user.isAdmin || user.isStaff) {
        const token = res.data.token
        try {
          const authDetails = await User.api.validateToken(res.data.token)
          if (authDetails['needsVerification']) {
            this.$router.push({
              name: 'TwoFactorAuth',
              query: this.$route.query,
              params: { token: token },
            })
            return
          } else {
            user = await User.api.getSelf({ token: token, tokenType: 'Token' })
          }
        } catch (err) {
          console.log(err)
          this.errors = err.response.data
          this.loading = false
          return
        }
      } else if (user.isEmployee) {
        user = Employee.fromAPI(res.data)
        this.$language.current = user.preferredLanguage
      }

      // At this point the user is either an employee or has passed two factor authentication, store their data to app state
      this.$store.dispatch('updateUser', user)
      this.$store.dispatch('updateUserToken', res.data.token)
      this.$store.commit('UPDATE_CURRENT_LANGUAGE', user.preferredLanguage)

      // Route to correct next page
      if (this.$route.query.redirect) {
        this.$router.push({ path: this.$route.query.redirect })
      } else if (this.$route.query.next) {
        this.$router.push({ path: this.$route.query.next })
      } else {
        this.navigateToLoggedInHomeScreen()
      }
      this.loading = false
    },
    navigateToLoggedInHomeScreen() {
      if (this.user.isStaff) {
        this.$router.push({ name: 'StaffReports' })
      } else if (this.user.isAdmin) {
        this.$router.push({ name: 'StaffReports' })
      } else if (this.user.isEmployee) {
        this.$router.push({ name: 'Home', params: { fromLogin: true } })
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars']),
    text() {
      return this.$gettext('Sign In')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.login {
  @include screen-container();
  justify-content: flex-start;
}

.logo-wrapper-gc {
  height: 4rem;
  width: 14rem;
  margin-bottom: 1rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100vh;
  padding: 30px 20px 50px 20px;
}
.md-checkbox {
  display: flex;
}

.forgot-password {
  font-size: 0.8rem;
  text-decoration: underline;
  text-decoration-style: dashed;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
}

.button {
  @include primary-button(var(--dark-brand-color));
}

.create-account-cta {
  margin-top: 1vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  span {
    font-size: 0.8rem;
  }
}

.ruby-font {
  color: $ruby;
}
.api-error {
  font-size: 0.7rem;
}
</style>
